<template>
  <div class="wrapper" @click="$emit('link-clicked')">
    <!-- child item is an external link -->
    <a id="category-item-link" v-if="externalLink" :href="externalLink" target="_blank" class="category-item link" :class="{ 'has-icon': icon }">
      <span v-if="icon" class="material-symbols-outlined child-item-icon">{{ icon }}</span>
      <p class="child-item-text">{{ label }}</p>
    </a>

    <!-- child item is an internal link -->
    <router-link v-else :id="`${routeName}-link`" :to="{ name: routeName }" class="category-item" :class="{ active: isActive, 'has-icon': icon }">
      <span v-if="icon" class="material-symbols-outlined child-item-icon">{{ icon }}</span>
      <p v-if="!productName" class="child-item-text">{{ label }}</p>

      <!-- child item is a matches page link -->
      <div v-if="productName" class="matches-children">
        <p class="child-item-text">{{ label }}</p>
      </div>
    </router-link>
  </div>
</template>

<script>
export default {
  props: {
    label: {
      type: String
    },
    routeName: {
      type: String
    },
    externalLink: {
      type: String
    },
    productName: {
      type: String
    },
    // Only the collapsed rail's flyout shows icons; the expanded sub-menu stays text only.
    icon: {
      type: String
    }
  },
  computed: {
    isActive() {
      return this.$route?.name === this.routeName
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/swoop/_variables.scss';
@import '@/assets/styles/swoop/_mixins.scss';

$sidebar-hover: rgba(255, 255, 255, 0.07);
$sidebar-selected-text: var(--color-secondary-200);
$sidebar-selected-fill: color-mix(in srgb, var(--color-secondary-500) 26%, transparent);
$sidebar-sub-ink: rgba(255, 255, 255, 0.66);

.wrapper {
  display: flex;
  flex-direction: row;
  width: 100%;

  .category-item {
    display: block;
    width: 100%;
    cursor: pointer;
    text-decoration: none;
    font-size: 14px;
    font-weight: 400;
    color: $sidebar-sub-ink;
    // Sub-items hang under the parent label, which sits 14px of row padding past the 22px icon and its 12px gap.
    padding: 9px 14px 9px 48px;
    margin-bottom: 2px;
    border-radius: 8px;
    transition: background-color 0.12s ease, color 0.12s ease;

    &:hover {
      text-decoration: none;
      background-color: $sidebar-hover;
      color: var(--color-white);

      .child-item-text {
        color: var(--color-white);
      }
    }

    &:focus-visible {
      outline: 1px solid var(--color-white);
      outline-offset: -1px;
    }

    p {
      margin: 0;
    }

    .child-item-text {
      font-size: 14px;
      font-weight: inherit;
      color: inherit;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &.router-link-exact-active,
    &.active,
    &.router-link-exact-active:hover,
    &.active:hover {
      background-color: $sidebar-selected-fill;
      color: $sidebar-selected-text;
      font-weight: 700;

      .child-item-text {
        color: $sidebar-selected-text;
      }
    }

    .matches-children {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    // Flyout rows sit at full width with an icon, so they drop the hanging indent.
    &.has-icon {
      display: flex;
      align-items: center;
      gap: 12px;
      height: 40px;
      padding: 0 8px;
      font-size: 16px;

      .child-item-icon {
        font-size: 22px;
        flex-shrink: 0;
        font-variation-settings: 'FILL' 0;
      }

      .child-item-text {
        font-size: 16px;
        line-height: 22px;
        letter-spacing: -0.4px;
      }

      &:hover .child-item-icon {
        font-variation-settings: 'FILL' 1;
      }

      // Flyout rows are sidebar-item instances, so unlike expanded sub-items they take the edge bar.
      &.router-link-exact-active,
      &.active {
        box-shadow: inset 3px 0 0 var(--color-secondary-500);

        .child-item-icon {
          color: $sidebar-selected-text;
          font-variation-settings: 'FILL' 1;
        }
      }
    }
  }
}
</style>
